import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Stage, Layer, Image, Text as KonvaText, Group, Rect, Line } from 'react-konva'; // Add Line import
import { ZoomIn, ZoomOut } from "@geist-ui/icons"; // Replace Plus, Minus with ZoomIn, ZoomOut
import useImage from 'use-image';
import './Table.css'; // Assuming you have styles for zoom-controls etc.
import MapSelector from './MapSelector';
import EnemySelector from './EnemySelector';  // Add this import
import TableSidebar from './TableSidebar';
import { createInteractionMode } from './interactionMode';
import SettingsWindow from './SettingsWindow'; // Add to imports
import { DrawingLayer, useDrawing } from './Drawing'; // Add DrawingLayer and useDrawing import
import { getAssetUrl } from '../../../utils/assetUtils';

// --- Constants ---
const BASE_TOKEN_SIZE = 50; // Increased by 10% from 50
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 5.0;
const ZOOM_STEP_BUTTON = 1.2; // Zoom factor for buttons
const ZOOM_STEP_WHEEL = 1.05; // Smaller zoom factor for wheel
const COMBAT_STATE_KEY = 'om_play_combat_state';

const TableView = () => {
  // --- State ---
  const [stageSize, setStageSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight - 65 // Adjust based on your layout
  });
  const [stageScale, setStageScale] = useState(1);
  const [tokenPositions, setTokenPositions] = useState({}); // Remove initial token positions from state
  const [currentTokens, setCurrentTokens] = useState([]); // Add state for dynamic tokens
  const [mapImage, setMapImage] = useState(null); // State for map image
  const [stageIsDraggable, setStageIsDraggable] = useState(false);
  const [currentSection, setCurrentSection] = useState(null); // Add state for current section

  // Add state for player name
  const [playerName, setPlayerName] = useState('Player');
  const [playerImage, setPlayerImage] = useState(null); // Add state for player image

  // Add this after other state declarations
  const [tokenHitPoints, setTokenHitPoints] = useState({});

  // Add after other state declarations
  const [editingTokenHP, setEditingTokenHP] = useState(null);

  // Add new state variables for the HTML input
  const [inputPosition, setInputPosition] = useState({ x: 0, y: 0 });
  const [inputValue, setInputValue] = useState('');
  const [showInput, setShowInput] = useState(false);

  // Add a ref for the input element
  const inputRef = useRef(null);

  // Add this near your other state variables
  const [editingHPPart, setEditingHPPart] = useState('current'); // 'current' or 'max'

  // Add a new state variable for map size
  const [mapSize, setMapSize] = useState(1.0);

  // Load player name and image from localStorage on mount
  useEffect(() => {
    const savedName = localStorage.getItem('playerName');
    const savedImage = localStorage.getItem('playerImage');
    if (savedName) {
      setPlayerName(savedName);
    }
    if (savedImage) {
      setPlayerImage(savedImage);
    }
  }, []);

  // Visibility
  const [isMapSelectorVisible, setIsMapSelectorVisible] = useState(true);
  const [isEnemySelectorVisible, setIsEnemySelectorVisible] = useState(true);
  const [isSettingsVisible, setIsSettingsVisible] = useState(false); // Add new state variables

  // UI Positions
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [selectorPos, setSelectorPos] = useState({ x: 100, y: 50 }); // State for map selector position
  const [enemySelectorPos, setEnemySelectorPos] = useState({ x: 100, y: 150 }); // Position it next to map selector
  const [settingsPos, setSettingsPos] = useState({ x: 100, y: 230 }); // Add new state variables

  // Create interaction mode instance
  const [interactionModeObject, setInteractionModeObject] = useState(() => createInteractionMode());
  const [interactionMode, setInteractionMode] = useState(interactionModeObject.getMode());

  // Add mouse button state tracking
  const [isMiddleMouseDown, setIsMiddleMouseDown] = useState(false);

  // Add new state for selected token
  const [selectedTokenId, setSelectedTokenId] = useState(null);
  const [selectedEnemy, setSelectedEnemy] = useState(null); // Add state for selected enemy

  // Add new state for ruler measurement
  const [rulerStartPoint, setRulerStartPoint] = useState(null);
  const [rulerEndPoint, setRulerEndPoint] = useState(null);
  const [isRulerActive, setIsRulerActive] = useState(false);

  // Add mapLoaded to the state variables near the top of the component
  const [mapLoaded, setMapLoaded] = useState(false);

  const [loadedTokenImages, setLoadedTokenImages] = useState({});

  // --- Refs ---
  const stageRef = useRef(null); // Ref to access stage methods

  // --- Effects ---

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setStageSize({
        width: window.innerWidth,
        height: window.innerHeight - 65
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Center map initially when map image loads or stage size changes
  useEffect(() => {
    if (mapImage) {
       const initialX = (stageSize.width - mapImage.width * stageScale * mapSize) / 2;
       const initialY = (stageSize.height - mapImage.height * stageScale * mapSize) / 2;
       if (stagePos.x === 0 && stagePos.y === 0) {
           setStagePos({ x: initialX, y: initialY });
       }
    }
  }, [mapImage, stageSize.width, stageSize.height, stageScale, mapSize]);

  // Subscribe to changes in interaction mode
  useEffect(() => {
    const unsubscribe = interactionModeObject.subscribe(({ mode, isTokenDragging }) => {
      setStageIsDraggable(mode === 'pan');
      setInteractionMode(mode);
      // Any other state updates needed
    });
    return unsubscribe;
  }, [interactionModeObject]);

  // Add this new handler to prevent context menu
  useEffect(() => {
    const container = stageRef.current?.container();
    if (container) {
      const preventDefault = (e) => e.preventDefault();
      container.addEventListener('contextmenu', preventDefault);
      return () => container.removeEventListener('contextmenu', preventDefault);
    }
  }, []);

  // Add this useEffect hook after your other useEffects

  // Add a useEffect to handle clicks outside the input element as confirmation
  useEffect(() => {
    // Function to handle clicks anywhere in the document
    const handleDocumentClick = (e) => {
      if (showInput && inputRef.current && !inputRef.current.contains(e.target)) {
        // Click was outside the input - treat as confirmation
        handleHitPointsUpdate(editingTokenHP, inputValue);
        setShowInput(false);
      }
    };
    
    // Only add the listener when we're showing the input
    if (showInput) {
      // Small delay to prevent the same click that opened the input from closing it
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleDocumentClick);
      }, 10);
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleDocumentClick);
      };
    }
  }, [showInput, editingTokenHP, inputValue]);

  // Reset ruler when changing interaction mode
  useEffect(() => {
    if (interactionMode !== 'ruler') {
      setRulerStartPoint(null);
      setRulerEndPoint(null);
      setIsRulerActive(false);
    }
  }, [interactionMode]);

  const saveCombatState = useCallback(() => {
    if (!currentSection || !mapLoaded) return;
    
    try {
      const stateToSave = {
        mapSection: currentSection,
        tokenPositions,
        tokenHitPoints,
        stagePos,
        stageScale,
        selectedTokenId,
        mapSize // Add map size to saved state
      };
      
      localStorage.setItem(COMBAT_STATE_KEY, JSON.stringify(stateToSave));
      console.log("Combat state saved");
    } catch (error) {
      console.error("Error saving combat state:", error);
    }
  }, [currentSection, tokenPositions, tokenHitPoints, stagePos, stageScale, selectedTokenId, mapLoaded, mapSize]);

  const handleMapSelect = (mapPath, tokens, section) => {
    if (!mapPath) return;
    
    setMapLoaded(false);
    setLoadedTokenImages({}); // Reset token loading state
    
    const img = new window.Image();
    img.crossOrigin = 'Anonymous'; // Add cross-origin for external images
    img.src = getAssetUrl(mapPath, { forceWebP: true });
    
    img.onerror = () => {
      img.src = getAssetUrl(mapPath, { forceWebP: false });
    };
    
    img.onload = () => {
      setMapImage(img);
      setCurrentSection(section);
      
      // Get mapSize from section or default to 1.0
      const size = section.mapSize || 1.0;
      setMapSize(size);
      
      // Adjust initial positioning based on map size
      const initialX = (stageSize.width - img.width * stageScale * size) / 2;
      const initialY = (stageSize.height - img.height * stageScale * size) / 2;
      setStagePos({ x: initialX, y: initialY });

      setTokenPositions({});
      if (tokens) {
        const newTokenData = {};
        const newHitPoints = {};
        const newLoadedStatus = {};
        
        tokens.forEach(token => {
          const tokenId = token.id;
          newLoadedStatus[tokenId] = false; // Start with not loaded
          
          const img = new window.Image();
          img.crossOrigin = 'Anonymous'; // Add cross-origin for external images
          const tokenSrc = token.id === 'player' && playerImage ? playerImage : getAssetUrl(token.image, { forceWebP: true });
          
          // Scale token positions based on map size
          const scaledPosition = {
            x: token.initialPosition.x * size,
            y: token.initialPosition.y * size
          };
          
          newTokenData[tokenId] = {
            pos: scaledPosition,
            image: null, // Start with null until loaded
            name: token.name || tokenId
          };
          
          newHitPoints[tokenId] = {
            current: token.hitPoints || undefined,
            max: token.hitPoints || undefined
          };
          
          img.onerror = () => {
            console.error("Failed to load token image:", token.image);
            const fallbackSrc = token.id === 'player' && playerImage ? playerImage : getAssetUrl(token.image, { forceWebP: false });
            img.src = fallbackSrc;
          };
          
          img.onload = () => {
            setTokenPositions(prev => ({
              ...prev,
              [tokenId]: {
                ...prev[tokenId],
                image: img
              }
            }));
            
            setLoadedTokenImages(prev => ({
              ...prev,
              [tokenId]: true
            }));
          };
          
          img.src = tokenSrc;
        });
        
        setTokenPositions(newTokenData);
        setTokenHitPoints(newHitPoints);
        setLoadedTokenImages(newLoadedStatus);
      }
      
      setMapLoaded(true);
      
      setTimeout(() => {
        saveCombatState();
      }, 300);
    };
  };

  const handleResetMap = () => {
    if (currentSection) {
      handleMapSelect(
        currentSection.image, 
        [...(currentSection.tokens || []), ...(currentSection.players || [])],
        currentSection
      );
    }
  };

  useEffect(() => {
    try {
      console.log("Attempting to load saved combat state");
      const savedState = localStorage.getItem(COMBAT_STATE_KEY);
      if (savedState) {
        const state = JSON.parse(savedState);
        console.log("Found saved state:", state);
        
        if (state.mapSection && state.mapSection.image) {
          setLoadedTokenImages({}); // Reset token loading state
          
          const img = new window.Image();
          img.crossOrigin = 'Anonymous'; // Add cross-origin for external images
          img.src = getAssetUrl(state.mapSection.image, { forceWebP: true });
          
          img.onerror = () => {
            img.src = getAssetUrl(state.mapSection.image, { forceWebP: false });
          };
          
          img.onload = () => {
            setMapImage(img);
            setCurrentSection(state.mapSection);
            
            // Restore map size from saved state or section
            if (state.mapSize) {
              setMapSize(state.mapSize);
            } else if (state.mapSection.mapSize) {
              setMapSize(state.mapSection.mapSize);
            } else {
              setMapSize(1.0);
            }
            
            setMapLoaded(true);
            
            if (state.tokenPositions) {
              const tokens = state.mapSection.tokens || [];
              const players = state.mapSection.players || [];
              const allTokens = [...tokens, ...players];
              const newTokenData = {};
              const newLoadedStatus = {};
              
              // Get the map size factor
              const size = state.mapSize || state.mapSection.mapSize || 1.0;
              
              allTokens.forEach(token => {
                const tokenId = token.id;
                newLoadedStatus[tokenId] = false; // Start with not loaded
                
                // Use saved position or scale the initial position based on map size
                const position = state.tokenPositions[tokenId]?.pos || 
                  {
                    x: token.initialPosition.x * size,
                    y: token.initialPosition.y * size
                  };
                
                newTokenData[tokenId] = {
                  pos: position,
                  image: null, // Start with null until loaded
                  name: token.name || tokenId
                };
                
                const tokenImg = new window.Image();
                tokenImg.crossOrigin = 'Anonymous'; // Add cross-origin for external images
                const tokenSrc = token.id === 'player' && playerImage ? playerImage : getAssetUrl(token.image, { forceWebP: true });
                
                tokenImg.onerror = () => {
                  const fallbackSrc = token.id === 'player' && playerImage ? playerImage : getAssetUrl(token.image, { forceWebP: false });
                  tokenImg.src = fallbackSrc;
                };
                
                tokenImg.onload = () => {
                  setTokenPositions(prev => ({
                    ...prev,
                    [tokenId]: {
                      ...prev[tokenId],
                      image: tokenImg
                    }
                  }));
                  
                  setLoadedTokenImages(prev => ({
                    ...prev,
                    [tokenId]: true
                  }));
                };
                
                tokenImg.src = tokenSrc;
              });
              
              setTokenPositions(newTokenData);
              setLoadedTokenImages(newLoadedStatus);
            }
            
            // Restore other state
            if (state.tokenHitPoints) {
              setTokenHitPoints(state.tokenHitPoints);
            }
            
            if (state.stagePos) {
              setStagePos(state.stagePos);
            }
            
            if (state.stageScale) {
              setStageScale(state.stageScale);
            }
            
            if (state.selectedTokenId) {
              setSelectedTokenId(state.selectedTokenId);
            }
          };
        }
      }
    } catch (error) {
      console.error("Error loading combat state:", error);
    }
  }, [playerImage]); // Only run once on initial load

  // Add this effect to save state when it changes
  useEffect(() => {
    // Only save if we have a map loaded
    if (currentSection && mapLoaded) {
      // Use debounce to avoid saving too frequently
      const saveTimer = setTimeout(() => {
        saveCombatState();
      }, 500);
      
      return () => clearTimeout(saveTimer);
    }
  }, [currentSection, tokenPositions, tokenHitPoints, stagePos, stageScale, selectedTokenId, mapLoaded, saveCombatState]);

  // --- Handlers ---

  // Called when a token drag starts
  const handleTokenDragStart = useCallback(() => {
    interactionModeObject.setTokenDragging(true); // Set flag to disable stage dragging
  }, [interactionModeObject]);

  // Called when a token drag ends
  const handleTokenDragEnd = useCallback((e, tokenId) => {
    interactionModeObject.setTokenDragging(false);
    setTokenPositions(prev => ({
      ...prev,
      [tokenId]: {
        ...prev[tokenId],
        pos: {
          x: e.target.x(),
          y: e.target.y()
        }
      }
    }));
  }, [interactionModeObject]);

  // Called when the stage itself is dragged (panning)
  const handleStageDragEnd = useCallback((e) => {
    if (stageIsDraggable && e.target === stageRef.current) {
       setStagePos(e.target.position());
    }
  }, [stageIsDraggable]);

  // Generic zoom function
  const performZoom = useCallback((zoomCenter, scaleMultiplier) => {
    const stage = stageRef.current;
    if (!stage) return;

    const oldScale = stage.scaleX();
    const pointTo = {
      x: (zoomCenter.x - stage.x()) / oldScale,
      y: (zoomCenter.y - stage.y()) / oldScale,
    };
    let newScale = oldScale * scaleMultiplier;
    newScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newScale));

    if (Math.abs(newScale - oldScale) < 0.001) return;

    setStageScale(newScale);

    const newPos = {
      x: zoomCenter.x - pointTo.x * newScale,
      y: zoomCenter.y - pointTo.y * newScale,
    };
    setStagePos(newPos);
  }, [MIN_ZOOM, MAX_ZOOM]); // setStageScale, setStagePos are stable

  // Handle Zooming with Buttons
  const handleButtonZoom = (direction) => {
    const stage = stageRef.current;
    if (!stage) return;
    const scaleMultiplier = direction === 'in' ? ZOOM_STEP_BUTTON : 1 / ZOOM_STEP_BUTTON;
    const center = {
      x: stageSize.width / 2,
      y: stageSize.height / 2,
    };
    performZoom(center, scaleMultiplier);
  };

   // Handle Zooming with Mouse Wheel
   const handleWheelZoom = useCallback((e) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    const scaleMultiplier = e.evt.deltaY > 0 ? 1 / ZOOM_STEP_WHEEL : ZOOM_STEP_WHEEL;
    performZoom(pointer, scaleMultiplier);
   }, [performZoom]);

  // Handle drag end for map selector
  const handleSelectorDragEnd = (newPos) => {
    setSelectorPos(newPos);
  };

  const handleEnemySelect = useCallback((enemyPath, enemyId) => {
    if (!enemyPath || !currentSection) return;

    // Find the selected enemy in the statblocks
    const enemyStatblock = currentSection.statblocks.find(
      sb => sb.id === enemyId
    );

    if (enemyStatblock) {
      // Update the preview image in EnemySelector
      setSelectedEnemy(enemyId);
      // Select the corresponding token if it exists
      setSelectedTokenId(enemyId);
    }
  }, [currentSection]);

  const handleToggleMapSelector = () => {
    setIsMapSelectorVisible(!isMapSelectorVisible);
  };

  const handleToggleEnemySelector = () => {
    setIsEnemySelectorVisible(!isEnemySelectorVisible);
  };

  const handleModeChange = (mode) => {
    interactionModeObject.setMode(mode);
  };

  const handleSettingsDragEnd = (newPos) => {
    setSettingsPos(newPos);
  };

  const handleToggleSettings = () => {
    setIsSettingsVisible(!isSettingsVisible);
  };

  const handleSettingsSave = () => {
    // Reload player name and image from localStorage
    const savedName = localStorage.getItem('playerName');
    const savedImage = localStorage.getItem('playerImage');
    
    if (savedName) {
      setPlayerName(savedName);
    }
    if (savedImage) {
      setPlayerImage(savedImage);
    }

    // Refresh tokens with new player token
    if (currentSection) {
      handleMapSelect(currentSection.image, [...currentSection.tokens, ...currentSection.players], currentSection);
    }
  };

  // Update mouse button handlers
  const handleMouseDown = (e) => {
    if (e.evt.button === 2) { // Right mouse button
      e.evt.preventDefault();
      setIsMiddleMouseDown(true);
      const stage = stageRef.current;
      if (stage) {
        stage.container().style.cursor = 'grabbing';
      }
    } else if (interactionMode === 'draw' && e.evt.button === 0) { // Left click in draw mode
      handleDrawingMouseDown(e);
    } else if (interactionMode === 'ruler' && e.evt.button === 0) { // Left click in ruler mode
      // Get pointer position in stage coordinates
      const stage = stageRef.current;
      const pointerPos = stage.getPointerPosition();
      if (pointerPos) {
        // Convert to stage coordinates
        const stagePoint = {
          x: (pointerPos.x - stage.x()) / stage.scaleX(),
          y: (pointerPos.y - stage.y()) / stage.scaleY()
        };
        setRulerStartPoint(stagePoint);
        setRulerEndPoint(stagePoint); // Initially same as start
        setIsRulerActive(true);
      }
    }
  };

  const handleMouseMove = (e) => {
    if (isMiddleMouseDown) {
      const newPos = {
        x: stagePos.x + e.evt.movementX,
        y: stagePos.y + e.evt.movementY
      };
      setStagePos(newPos);
    } else if (interactionMode === 'draw') {
      handleDrawingMouseMove(e);
    } else if (interactionMode === 'ruler' && isRulerActive) {
      // Update end point during ruler measurement
      const stage = stageRef.current;
      const pointerPos = stage.getPointerPosition();
      if (pointerPos) {
        // Convert to stage coordinates
        const stagePoint = {
          x: (pointerPos.x - stage.x()) / stage.scaleX(),
          y: (pointerPos.y - stage.y()) / stage.scaleY()
        };
        setRulerEndPoint(stagePoint);
      }
    }
  };

  const handleMouseUp = (e) => {
    if (e.evt.button === 2) {
      setIsMiddleMouseDown(false);
      const stage = stageRef.current;
      if (stage) {
        stage.container().style.cursor = 'default';
      }
    } else if (interactionMode === 'draw') {
      handleDrawingMouseUp(e);
    } else if (interactionMode === 'ruler' && isRulerActive && e.evt.button === 0) {
      // Complete the measurement but keep it visible
      setIsRulerActive(false);
    }
  };

  // Update the handleTokenClick function
  const handleTokenClick = useCallback((e, tokenId) => {
    if (interactionMode === 'click') {
      e.cancelBubble = true;
      setSelectedTokenId(tokenId === selectedTokenId ? null : tokenId);
      
      // If it's an enemy token, find and select its statblock
      if (tokenId) {
        const enemyStatblock = currentSection?.statblocks?.find(
          sb => sb.id.toLowerCase() === tokenId.toLowerCase()
        );
        
        if (enemyStatblock) {
          setIsEnemySelectorVisible(true);
          setSelectedEnemy(enemyStatblock.id);
        }
      }
    }
  }, [selectedTokenId, interactionMode, currentSection]);

  // Add deselection when clicking on stage
  const handleStageClick = useCallback((e) => {
    if (e.target === e.target.getStage()) {
      setSelectedTokenId(null);
    }
  }, []);

  const handleHitPointChange = (tokenId, change) => {
    setTokenHitPoints(prev => {
      const current = prev[tokenId]?.current || 0;
      return {
        ...prev,
        [tokenId]: {
          ...prev[tokenId],
          current: Math.max(0, current + change)
        }
      };
    });
  };

  const handleHitPointsUpdate = (tokenId, newValue) => {
    const numValue = parseInt(newValue) || 0;
    setTokenHitPoints(prev => ({
      ...prev,
      [tokenId]: {
        ...prev[tokenId],
        [editingHPPart]: Math.max(0, numValue)
      }
    }));
    setEditingTokenHP(null);
  };

  // Update this helper function
  const getHPColor = (current, max) => {
    // If HP is not set (undefined), use amber color
    if (current === undefined || max === undefined) return "#FFC107"; // Amber for unknown HP
    
    // If current is exactly 0, return red
    if (current === 0) return "#636363"; // Dark Grey for 0 HP and initial state
    
    // If max is 0, use default green
    if (max === 0) return "#15ff00";
    
    const ratio = current / max;
    
    if (ratio >= 0.66) return "#006400"; // Green for high health (>= 66%)
    if (ratio >= 0.33) return "#C49102"; // Yellow for medium health (33-66%)
    return "#8B0000"; // Red for low health (< 33%)
  };

  // Function to calculate distance in feet
  const calculateDistance = (point1, point2) => {
    if (!point1 || !point2) return 0;
    
    const pixelDistance = Math.sqrt(
      Math.pow(point2.x - point1.x, 2) + Math.pow(point2.y - point1.y, 2)
    );
    
    // Convert pixel distance to feet, adjusting for map size
    const feetPerToken = 4.3;
    const feetPerPixel = (feetPerToken / BASE_TOKEN_SIZE) / mapSize;
    
    return pixelDistance * feetPerPixel;
  };

  // --- Token Data ---
  const tokens = Object.entries(tokenPositions).map(([id, data]) => ({
    id,
    image: data.image,
    pos: data.pos
  }));

  // Calculate offset based on the fixed base size for centering
  const tokenOffset = BASE_TOKEN_SIZE / 2;

  // Add drawing hooks with clearDrawing
  const { lines, isDrawing, handleMouseDown: handleDrawingMouseDown, handleMouseMove: handleDrawingMouseMove, handleMouseUp: handleDrawingMouseUp, clearDrawing } = useDrawing(stageRef);

  // --- Render ---
  return (
    <div className="table-page" style={{ padding: 0, overflow: 'hidden' }}>
      <TableSidebar 
        onToggleMapSelector={handleToggleMapSelector}
        onToggleEnemySelector={handleToggleEnemySelector}
        onToggleSettings={handleToggleSettings}
        isMapVisible={isMapSelectorVisible}
        isEnemyVisible={isEnemySelectorVisible}
        interactionMode={interactionMode}
        onModeChange={handleModeChange}
        onClearDrawings={clearDrawing} // Add this line
        onResetMap={handleResetMap} // Add this prop
      />
      
      {/* Add settings window */}
      {isSettingsVisible && (
        <SettingsWindow
          position={settingsPos}
          onDragEnd={handleSettingsDragEnd}
          onSettingsSave={handleSettingsSave}
          onClose={() => setIsSettingsVisible(false)}  // Add this line
        />
      )}
      
      {isMapSelectorVisible && (
        <MapSelector 
          position={selectorPos}
          onDragEnd={handleSelectorDragEnd}
          onMapSelect={handleMapSelect}
          onClose={() => setIsMapSelectorVisible(false)}
        />
      )}
      
      {isEnemySelectorVisible && (
        <EnemySelector 
          position={enemySelectorPos}
          onDragEnd={(newPos) => setEnemySelectorPos(newPos)}
          onEnemySelect={handleEnemySelect}
          currentSection={currentSection}
          onClose={() => setIsEnemySelectorVisible(false)}
          selectedTokenId={selectedTokenId} // Add this prop
        />
      )}

      <div className="zoom-controls">
        <button className="zoom-button zoom-in" onClick={() => handleButtonZoom('in')}>
          <ZoomIn size={20} />
        </button>
        <button className="zoom-button zoom-out" onClick={() => handleButtonZoom('out')}>
          <ZoomOut size={20} />
        </button>
      </div>
      <Stage
        ref={stageRef}
        width={stageSize.width}
        height={stageSize.height}
        scaleX={stageScale}
        scaleY={stageScale}
        x={stagePos.x}
        y={stagePos.y}
        draggable={interactionMode === 'pan'}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleStageClick}
        onDragEnd={handleStageDragEnd}
        onWheel={handleWheelZoom}
      >
        <Layer>
          {/* Map Image */}
          {mapImage && (
             <Image
                image={mapImage}
                x={0}
                y={0}
                width={mapImage.width * mapSize}
                height={mapImage.height * mapSize}
                listening={false}
                shadowColor="rgba(0, 0, 0, 0.9)"    // Increased opacity from 0.1 to 0.2
                shadowBlur={20}                      // Increased blur from 10 to 20
                shadowOffset={{ x: 0, y: 8 }}        // Increased y offset from 4 to 8
                shadowOpacity={0.5}                  // Keep same opacity
             />
          )}

          {/* Render Tokens */}
          {tokens.map(token => (
            token.pos && (
              <React.Fragment key={token.id}>
                <Group
                  x={token.pos.x}
                  y={token.pos.y}
                  draggable={interactionMode === 'click'}
                  onClick={(e) => handleTokenClick(e, token.id)}
                  onTap={(e) => handleTokenClick(e, token.id)}
                  onDragStart={(e) => {
                    handleTokenDragStart();
                    e.target.to({
                      scaleX: 1.1,
                      scaleY: 1.1,
                      shadowColor: 'black',
                      shadowBlur: 10,
                      shadowOpacity: 0.3,
                      duration: 0.1
                    });
                  }}
                  onDragEnd={(e) => {
                    handleTokenDragEnd(e, token.id);
                    e.target.to({
                      scaleX: 1,
                      scaleY: 1,
                      shadowBlur: 0,
                      shadowOpacity: 0,
                      duration: 0.1
                    });
                  }}
                >
                  {/* Only render the token image if it's loaded */}
                  {token.image && loadedTokenImages[token.id] && (
                    <Image
                      image={token.image}
                      width={BASE_TOKEN_SIZE}
                      height={BASE_TOKEN_SIZE}
                      offsetX={tokenOffset}
                      offsetY={tokenOffset}
                      stroke={selectedTokenId === token.id ? "#df2d11" : "#D3AF37"}
                      strokeWidth={3}
                      cornerRadius={25}
                    />
                  )}

                  {/* Placeholder if image is not loaded yet */}
                  {(!token.image || !loadedTokenImages[token.id]) && (
                    <Rect
                      width={BASE_TOKEN_SIZE}
                      height={BASE_TOKEN_SIZE}
                      offsetX={tokenOffset}
                      offsetY={tokenOffset}
                      fill="#888888"
                      stroke={selectedTokenId === token.id ? "#df2d11" : "#D3AF37"}
                      strokeWidth={3}
                      cornerRadius={25}
                    />
                  )}

                  {/* Add this new overlay for 0 HP tokens */}
                  {tokenHitPoints[token.id]?.current === 0 && (
                    <Rect
                      width={BASE_TOKEN_SIZE}
                      height={BASE_TOKEN_SIZE}
                      offsetX={tokenOffset}
                      offsetY={tokenOffset}
                      fill="black"
                      opacity={0.8}
                      cornerRadius={25}
                    />
                  )}

                  <KonvaText
                    text={token.id === 'player' ? 
                      playerName : 
                      tokenPositions[token.id]?.name || token.id.replace(/_/g, ' ').replace(/\d+$/, ' #$&')}
                    y={BASE_TOKEN_SIZE / 2 + 5}
                    width={100}
                    align="center"
                    offsetX={50}
                    offsetY={5}
                    fontSize={14}
                    fill="#ffffff"
                    fontFamily="Crimson"
                    fontStyle="bold"
                    padding={8}
                    shadowColor="black"
                    shadowBlur={2}
                    shadowOffset={{ x: 1, y: 1 }}
                    shadowOpacity={0.8}
                  />

                  {/* HP Display with background */}
                  <Rect
                    y={-50}
                    width={58}
                    height={20}
                    offsetX={30}
                    fill={getHPColor(
                      tokenHitPoints[token.id]?.current || 0,
                      tokenHitPoints[token.id]?.max || 1
                    )}
                    cornerRadius={12}
                  />
                  <KonvaText
                    y={-50}
                    width={70}  // Reduced from 100 to 70
                    align="center"
                    offsetX={35}  // Adjusted from 51 to 35 to match new width
                    text={
                      tokenHitPoints[token.id] ? 
                      `${tokenHitPoints[token.id].current !== undefined ? tokenHitPoints[token.id].current : '_'} / ${tokenHitPoints[token.id].max !== undefined ? tokenHitPoints[token.id].max : '_'}` : 
                      "_ / _"
                    }
                    fontSize={14}
                    fill="#ffffff"
                    fontFamily="Crimson"
                    fontStyle="bold"
                    padding={4}  // Reduced from 8 to 4
                    shadowColor="black"
                    shadowBlur={2}
                    shadowOffset={{ x: 1, y: 1 }}
                    shadowOpacity={0.8}
                    onClick={(e) => {
                      e.cancelBubble = true;
                      setEditingTokenHP(token.id);
                      
                      // Calculate position for HTML input
                      const stage = stageRef.current;
                      const position = stage.getAbsolutePosition();
                      
                      // Determine if clicking on current or max HP part
                      const pointerX = stage.getPointerPosition().x;
                      const tokenX = token.pos.x * stageScale + position.x;
                      const midPoint = tokenX; // Center point between current and max
                      
                      // Check if click is on left (current) or right (max) side
                      const isClickOnMax = pointerX > midPoint;
                      setEditingHPPart(isClickOnMax ? 'max' : 'current');
                      
                      // Adjust position based on which part is being edited
                      const xOffset = isClickOnMax ? 10 : -60; // Moved right by 30px
                      setInputPosition({
                        x: tokenX + xOffset,
                        y: (token.pos.y - 45) * stageScale + position.y // Moved down by 5px (scaled)
                      });
                      
                      // Set input value based on which part is being edited
                      setInputValue(
                        isClickOnMax 
                          ? (tokenHitPoints[token.id]?.max || 0)
                          : (tokenHitPoints[token.id]?.current || 0)
                      );
                      setShowInput(true);
                      
                      // Focus the input after it renders
                      setTimeout(() => {
                        if (inputRef.current) {
                          inputRef.current.focus();
                          inputRef.current.select();
                        }
                      }, 10);
                    }}
                  />
                </Group>
              </React.Fragment>
            )
          ))}
          
          {/* Add Ruler Line and Distance Text */}
          {(rulerStartPoint && rulerEndPoint) && (
            <>
              <Line
                points={[
                  rulerStartPoint.x, 
                  rulerStartPoint.y, 
                  rulerEndPoint.x, 
                  rulerEndPoint.y
                ]}
                stroke="#ffffff"  // Changed from #df2d11 (red) to #ffffff (white)
                strokeWidth={2}
                dash={[5, 5]}
                listening={false}
              />
              <KonvaText
                x={(rulerStartPoint.x + rulerEndPoint.x) / 2}
                y={(rulerStartPoint.y + rulerEndPoint.y) / 2 - 15}
                text={`${calculateDistance(rulerStartPoint, rulerEndPoint).toFixed(1)} ft`}
                fontSize={16}
                fill="#ffffff"  // Changed from #df2d11 (red) to #ffffff (white)
                fontFamily="Crimson"
                fontStyle="bold"
                align="center"
                listening={false}
                shadowColor="black"  // Changed shadow color to black for better contrast with white text
                shadowBlur={3}
                shadowOpacity={0.8}
              />
            </>
          )}
          
          {/* Add the DrawingLayer */}
          <DrawingLayer lines={lines} />
        </Layer>
      </Stage>
      {showInput && (
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleHitPointsUpdate(editingTokenHP, inputValue);
              setShowInput(false);
            } else if (e.key === 'Escape') {
              setEditingTokenHP(null);
              setShowInput(false);
            }
          }}
          onBlur={() => {
            setEditingTokenHP(null);
            setShowInput(false);
          }}
          style={{
            position: 'absolute',
            left: `${inputPosition.x}px`,
            top: `${inputPosition.y}px`,
            width: '50px',
            textAlign: 'center',
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '8px',
            padding: '4px',
            fontSize: '14px',
            fontFamily: 'Crimson',
            fontWeight: 'bold',
            zIndex: 1000,
            // Add custom selection color styles
            caretColor: 'white',
            selectionBackground: 'rgba(255, 255, 255, 0.3)',
          }}
          className="hp-input"
        />
      )}
    </div>
  );
};

export default TableView;